import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DEAL_STAGE } from "../../lib/enums";
import {
  DEAL_CONVERSION_STAGE_ORDER,
  DEAL_STAGE_LABELS,
  DEAL_STAGE_ORDER,
  normalizeDealStageForWorkflow,
} from "../../lib/labels";
import { api, extractApiError } from "../../lib/api";
import { toast } from "sonner";
import { getAssigneeLabel } from "../../lib/tasks-assignees";
import {
  getDefaultProbabilityForDealStage,
  getInitialProbabilityForStage,
  getProbabilityOnStageChange,
} from "../../lib/deal-probability";
import {
  calculateDealArr,
  calculateDealMrr,
  calculateDealTcv,
  formatMoneyInput,
  parseMoneyInput,
} from "../../lib/deal-revenue";

const NONE = "__none__";
const EMPTY_FORM = {
  title: "",
  company_id: NONE,
  contact_id: NONE,
  value: "",
  billing_model: "one_time",
  currency: "EUR",
  one_time_amount: "",
  recurring_amount: "",
  recurring_interval: "",
  recurring_term_months: "",
  probability: getInitialProbabilityForStage(DEAL_STAGE.QUALIFICATION),
  stage: DEAL_STAGE.QUALIFICATION,
  expected_close_date: "",
  assigned_user_id: NONE,
  notes: "",
};

function parseNullableIntegerInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  return n;
}

function toDateInputValue(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

export function buildConversionInitialValuesFromLead(lead) {
  if (!lead) return null;
  return {
    title: lead.title ?? "",
    company_id: lead.company_id ?? null,
    contact_id: lead.contact_id ?? null,
    assigned_user_id: lead.assigned_user_id ?? null,
    notes: lead.description ?? "",
    stage: DEAL_STAGE.QUALIFICATION,
  };
}

export function getSelectableDealStages({ isConvert = false } = {}) {
  return isConvert ? DEAL_CONVERSION_STAGE_ORDER : DEAL_STAGE_ORDER;
}

export function buildInitialForm(initialDeal, initialValues = null) {
  const base = initialDeal
    ? {
        title: initialDeal.title ?? "",
        company_id: initialDeal.company_id ?? NONE,
        contact_id: initialDeal.contact_id ?? NONE,
        value: formatMoneyInput(initialDeal.value),
        billing_model: initialDeal.billing_model ?? "one_time",
        currency: initialDeal.currency ?? "EUR",
        one_time_amount: formatMoneyInput(
          initialDeal.one_time_amount ?? (initialDeal.billing_model === "one_time" ? initialDeal.value : null)
        ),
        recurring_amount: formatMoneyInput(initialDeal.recurring_amount),
        recurring_interval: initialDeal.recurring_interval ?? "",
        recurring_term_months:
          initialDeal.recurring_term_months == null ? "" : String(initialDeal.recurring_term_months),
        probability:
          initialDeal.probability == null
            ? getInitialProbabilityForStage(
                normalizeDealStageForWorkflow(initialDeal.stage ?? DEAL_STAGE.QUALIFICATION)
              )
            : String(initialDeal.probability),
        stage: normalizeDealStageForWorkflow(initialDeal.stage ?? DEAL_STAGE.QUALIFICATION),
        expected_close_date: toDateInputValue(initialDeal.expected_close_date),
        assigned_user_id: initialDeal.assigned_user_id ?? NONE,
        notes: initialDeal.notes ?? "",
      }
    : { ...EMPTY_FORM };

  if (!initialValues) return base;

  const merged = { ...base };
  if ("title" in initialValues) merged.title = initialValues.title ?? "";
  if ("company_id" in initialValues) merged.company_id = initialValues.company_id ?? NONE;
  if ("contact_id" in initialValues) merged.contact_id = initialValues.contact_id ?? NONE;
  if ("value" in initialValues) merged.value = formatMoneyInput(initialValues.value);
  if ("billing_model" in initialValues) merged.billing_model = initialValues.billing_model ?? "one_time";
  if ("currency" in initialValues) merged.currency = initialValues.currency ?? "EUR";
  if ("one_time_amount" in initialValues) merged.one_time_amount = formatMoneyInput(initialValues.one_time_amount);
  if ("recurring_amount" in initialValues) merged.recurring_amount = formatMoneyInput(initialValues.recurring_amount);
  if ("recurring_interval" in initialValues) merged.recurring_interval = initialValues.recurring_interval ?? "";
  if ("recurring_term_months" in initialValues) {
    merged.recurring_term_months =
      initialValues.recurring_term_months == null ? "" : String(initialValues.recurring_term_months);
  }
  if ("probability" in initialValues) {
    merged.probability =
      initialValues.probability == null
        ? getInitialProbabilityForStage(merged.stage)
        : String(initialValues.probability);
  }
  if ("stage" in initialValues) {
    merged.stage = normalizeDealStageForWorkflow(initialValues.stage ?? DEAL_STAGE.QUALIFICATION);
  }
  if (
    (!("probability" in initialValues) || initialValues.probability == null) &&
    (!merged.probability || String(merged.probability).trim() === "")
  ) {
    merged.probability = getInitialProbabilityForStage(merged.stage);
  }
  if ("expected_close_date" in initialValues) {
    merged.expected_close_date = toDateInputValue(initialValues.expected_close_date);
  }
  if ("assigned_user_id" in initialValues) {
    merged.assigned_user_id = initialValues.assigned_user_id ?? NONE;
  }
  if ("notes" in initialValues) merged.notes = initialValues.notes ?? "";
  return merged;
}

export default function DealFormDialog({
  open,
  onOpenChange,
  onSaved,
  mode = "create",
  initialDeal = null,
  dealId = null,
  convertLeadId = null,
  initialValues = null,
}) {
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";
  const isConvert = mode === "convert";
  const effectiveDealId = dealId || initialDeal?.id || null;
  const selectableStages = useMemo(
    () => getSelectableDealStages({ isConvert }),
    [isConvert]
  );
  const suggestedProbability = useMemo(
    () => getDefaultProbabilityForDealStage(form.stage),
    [form.stage]
  );
  const revenuePreview = useMemo(
    () => ({
      mrr: calculateDealMrr(form),
      arr: calculateDealArr(form),
      tcv: calculateDealTcv(form),
    }),
    [form]
  );

  useEffect(() => {
    if (open) {
      Promise.all([
        api.get("/api/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([])),
        api.get("/api/contacts").then((r) => setContacts(r.data)).catch(() => setContacts([])),
        api.get("/api/settings/users").then((r) => setUsers(r.data)).catch(() => setUsers([])),
      ]);
      setForm(buildInitialForm(initialDeal, initialValues));
    }
  }, [open, initialDeal, initialValues]);

  const handleStageChange = (nextStage) => {
    setForm((current) => ({
      ...current,
      stage: nextStage,
      probability: getProbabilityOnStageChange({
        currentProbability: current.probability,
        nextStage,
      }),
    }));
  };

  const applyProbabilitySuggestion = () => {
    setForm((current) => ({
      ...current,
      probability: getInitialProbabilityForStage(current.stage),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Bitte einen Titel angeben.");
    setSaving(true);
    try {
      const expectedCloseDate = form.expected_close_date
        ? `${form.expected_close_date}T00:00:00.000Z`
        : null;
      const valueNumber = parseMoneyInput(form.value);
      const oneTimeAmount = parseMoneyInput(form.one_time_amount);
      const recurringAmount = parseMoneyInput(form.recurring_amount);
      if (form.one_time_amount && oneTimeAmount == null) {
        return toast.error("Bitte einen gültigen einmaligen Betrag angeben.");
      }
      if (form.recurring_amount && recurringAmount == null) {
        return toast.error("Bitte einen gültigen wiederkehrenden Betrag angeben.");
      }
      const payload = {
        title: form.title,
        stage: form.stage,
        notes: form.notes,
        company_id: form.company_id === NONE ? null : form.company_id,
        contact_id: form.contact_id === NONE ? null : form.contact_id,
        assigned_user_id: form.assigned_user_id === NONE ? null : form.assigned_user_id,
        expected_close_date: expectedCloseDate,
        value: valueNumber,
        billing_model: form.billing_model,
        currency: form.currency || "EUR",
        one_time_amount: oneTimeAmount,
        recurring_amount: recurringAmount,
        recurring_interval: form.recurring_interval || null,
        recurring_term_months: parseNullableIntegerInput(form.recurring_term_months),
        probability: parseNullableIntegerInput(form.probability),
      };
      if (isEdit) {
        if (!effectiveDealId) throw new Error("missing-deal-id");
        const response = await api.patch(`/api/deals/${effectiveDealId}`, payload);
        toast.success("Deal aktualisiert.");
        onSaved?.(response.data);
      } else if (isConvert) {
        if (!convertLeadId) throw new Error("missing-lead-id");
        const response = await api.post(`/api/leads/${convertLeadId}/convert-to-deal`, payload);
        toast.success("Deal wurde aus Lead erstellt.");
        onSaved?.(response.data);
      } else {
        const response = await api.post("/api/deals", payload);
        toast.success("Deal erstellt.");
        onSaved?.(response.data);
      }
    } catch (err) {
      toast.error(
        extractApiError(
          err,
          isEdit
            ? "Deal konnte nicht aktualisiert werden."
            : isConvert
              ? "Lead konnte nicht in Deal umgewandelt werden."
              : "Deal konnte nicht erstellt werden."
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? "Deal bearbeiten"
              : isConvert
                ? "Lead in Deal umwandeln"
                : "Deal erstellen"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Fld label="Titel *" full><Input data-testid="deal-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus /></Fld>
          <Fld label="Firma" full>
            <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger><SelectValue placeholder="Keine Firma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Keine Firma</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Zugeordneter Kontakt" full>
            <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })}>
              <SelectTrigger><SelectValue placeholder="Kein Kontakt" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Kein Kontakt</SelectItem>
                {contacts.map((c) => {
                  const name = `${c.first_name || ""} ${c.last_name || ""}`.trim();
                  const label = name || c.email || "Unbekannter Kontakt";
                  return <SelectItem key={c.id} value={c.id}>{label}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Zuständig" full>
            <Select value={form.assigned_user_id} onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}>
              <SelectTrigger><SelectValue placeholder="Nicht zugewiesen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Nicht zugewiesen</SelectItem>
                {(users || []).map((u) => (
                  <SelectItem key={u.id} value={u.id}>{getAssigneeLabel(u)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Wert (€)">
            <Input
              type="text"
              inputMode="decimal"
              placeholder="8.000"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          </Fld>
          <Fld label="Abrechnungsmodell">
            <Select value={form.billing_model} onValueChange={(v) => setForm({ ...form, billing_model: v })}>
              <SelectTrigger><SelectValue placeholder="Abrechnungsmodell" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="one_time">Einmalig</SelectItem>
                <SelectItem value="recurring">Wiederkehrend</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </Fld>
          {(form.billing_model === "one_time" || form.billing_model === "hybrid") && (
            <Fld label="Einmaliger Betrag (€)">
              <Input
                type="text"
                inputMode="decimal"
                data-testid="deal-form-one-time-amount"
                placeholder="2.000"
                value={form.one_time_amount}
                onChange={(e) => setForm({ ...form, one_time_amount: e.target.value })}
              />
            </Fld>
          )}
          {(form.billing_model === "recurring" || form.billing_model === "hybrid") && (
            <>
              <Fld label="Wiederkehrender Betrag (€)">
                <Input
                  type="text"
                  inputMode="decimal"
                  data-testid="deal-form-recurring-amount"
                  placeholder="500"
                  value={form.recurring_amount}
                  onChange={(e) => setForm({ ...form, recurring_amount: e.target.value })}
                />
              </Fld>
              <Fld label="Intervall">
                <Select
                  value={form.recurring_interval || NONE}
                  onValueChange={(v) => setForm({ ...form, recurring_interval: v === NONE ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Intervall" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Nicht gesetzt</SelectItem>
                    <SelectItem value="monthly">Monatlich</SelectItem>
                    <SelectItem value="yearly">Jährlich</SelectItem>
                  </SelectContent>
                </Select>
              </Fld>
              <Fld label="Laufzeit (Monate, optional)">
                <Input
                  data-testid="deal-form-recurring-term"
                  value={form.recurring_term_months}
                  onChange={(e) => setForm({ ...form, recurring_term_months: e.target.value })}
                  placeholder="12"
                />
              </Fld>
            </>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Wahrscheinlichkeit (%)</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={form.probability}
              onChange={(e) => setForm({ ...form, probability: e.target.value })}
            />
            <div className="text-[11px] text-muted-foreground">
              Schätzung der aktuellen Abschlusswahrscheinlichkeit. Diese fließt in den
              gewichteten Forecast ein.
            </div>
          </div>
          <Fld label="Deal-Phase">
            <Select value={form.stage} onValueChange={handleStageChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {selectableStages.map((s) => <SelectItem key={s} value={s}>{DEAL_STAGE_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          <div className="md:col-span-2 rounded-lg border border-border/60 bg-muted/20 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="space-y-2">
                <div className="text-sm font-medium">Wahrscheinlichkeit einschätzen</div>
                <div className="text-xs text-muted-foreground">
                  Die Wahrscheinlichkeit beschreibt, wie realistisch ein Abschluss aus heutiger
                  Sicht ist. Sie beeinflusst den gewichteten Forecast.
                </div>
                <div className="text-xs text-muted-foreground">
                  Vorschlag für diese Phase: <span className="font-medium text-foreground">{suggestedProbability} %</span>
                </div>
                <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                  <li>Bedarf klar?</li>
                  <li>Budget realistisch oder bestätigt?</li>
                  <li>Entscheider involviert?</li>
                  <li>Konkreter nächster Schritt vereinbart?</li>
                  <li>Timing bekannt?</li>
                  <li>Wettbewerb oder starke Einwände?</li>
                </ul>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={applyProbabilitySuggestion}>
                Vorschlag übernehmen
              </Button>
            </div>
          </div>
          <div className="md:col-span-2 rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1">
            <div className="text-sm font-medium">Revenue-Zusammenfassung</div>
            <div className="text-xs text-muted-foreground">MRR: {formatMoneyInput(revenuePreview.mrr)}</div>
            <div className="text-xs text-muted-foreground">ARR: {formatMoneyInput(revenuePreview.arr)}</div>
            <div className="text-xs text-muted-foreground">
              TCV / Forecast-Wert: {formatMoneyInput(revenuePreview.tcv)}
            </div>
          </div>
          <Fld label="Erwarteter Abschluss">
            <Input
              type="date"
              value={form.expected_close_date}
              onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })}
            />
          </Fld>
          <Fld label="Notizen" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Fld>
          <DialogFooter className="md:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving}>Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="deal-form-submit">
              {saving ? "Speichern …" : isEdit ? "Speichern" : isConvert ? "Deal erstellen" : "Erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children, full = false }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
